import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import { Plus, Search, Edit, Trash2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Permissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [permissionNumber, setPermissionNumber] = useState('');
  const [tankIdsInput, setTankIdsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [editTankIdsInput, setEditTankIdsInput] = useState('');
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const { t, isRtl } = useLanguage();

  useEffect(() => {
    fetchPermissions();
    fetchUserRole();
  }, []);

  const fetchUserRole = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Failed to fetch user role');
    }
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = permissions.filter(p =>
        p.permission_number.toString().includes(searchTerm)
      );
      setFilteredPermissions(filtered);
    } else {
      setFilteredPermissions(permissions);
    }
  }, [searchTerm, permissions]);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPermissions(response.data);
      setFilteredPermissions(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        toast.error(t.sessionExpired);
        navigate('/login');
      } else {
        toast.error(t.somethingWentWrong);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePermission = async () => {
    const tankIds = tankIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    if (!permissionNumber || tankIds.length === 0) {
      toast.error(isRtl ? 'يرجى ملء جميع الحقول' : 'Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/permissions`,
        {
          permission_number: parseInt(permissionNumber),
          tank_ids: tankIds,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t.permissionCreated);
      setDialogOpen(false);
      setPermissionNumber('');
      setTankIdsInput('');
      fetchPermissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.somethingWentWrong);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPermission = (permission, e) => {
    e.stopPropagation();
    setEditingPermission(permission);
    setEditTankIdsInput(permission.tank_ids.join(', '));
    setEditDialogOpen(true);
  };

  const handleUpdatePermission = async () => {
    const tankIds = editTankIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    if (tankIds.length === 0) {
      toast.error(isRtl ? 'يرجى إدخال معرف خزان واحد على الأقل' : 'Please enter at least one tank ID');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/permissions/${editingPermission.permission_number}`,
        {
          permission_number: editingPermission.permission_number,
          tank_ids: tankIds,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t.permissionUpdated);
      setEditDialogOpen(false);
      setEditingPermission(null);
      setEditTankIdsInput('');
      fetchPermissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.somethingWentWrong);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePermission = async (permission, e) => {
    e.stopPropagation();
    
    if (!window.confirm(t.deletePermissionConfirm)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/permissions/${permission.permission_number}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t.permissionDeleted);
      fetchPermissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.somethingWentWrong);
    }
  };

  return (
    <Layout>
      <div data-testid="permissions-page" className="space-y-6">
        <div className={`flex flex-col md:flex-row md:justify-between md:items-center gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className={isRtl ? 'text-right' : ''}>
            <h1 className="text-4xl font-bold text-slate-900">{t.permissionsTitle}</h1>
            <p className="text-slate-600 mt-2">{t.permissionsSubtitle}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-permission-button"
                className={`bg-slate-900 hover:bg-slate-800 text-white ${isRtl ? 'flex-row-reverse' : ''}`}
              >
                <Plus className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                {t.createPermission}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader className="border-b border-blue-200 pb-4">
                <DialogTitle className={`text-2xl font-bold text-blue-900 ${isRtl ? 'text-right' : ''}`}>{t.createNewPermission}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                <div>
                  <Label className={isRtl ? 'block text-right' : ''}>{t.permissionNumber}</Label>
                  <Input
                    data-testid="permission-number-input"
                    type="number"
                    value={permissionNumber}
                    onChange={(e) => setPermissionNumber(e.target.value)}
                    placeholder={t.enterPermissionNumber}
                    className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                    dir={isRtl ? 'rtl' : 'ltr'}
                  />
                </div>

                <div>
                  <Label className={isRtl ? 'block text-right' : ''}>{t.tankIds}</Label>
                  <p className={`text-sm text-slate-600 mb-2 ${isRtl ? 'text-right' : ''}`}>
                    {t.tankIdsHelper}
                  </p>
                  <textarea
                    data-testid="tank-ids-input"
                    value={tankIdsInput}
                    onChange={(e) => setTankIdsInput(e.target.value)}
                    placeholder={t.tankIdsPlaceholder}
                    className={`w-full min-h-32 p-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-900 focus:border-transparent ${isRtl ? 'text-right' : ''}`}
                    dir={isRtl ? 'rtl' : 'ltr'}
                  />
                  <p className={`text-sm text-slate-500 mt-2 ${isRtl ? 'text-right' : ''}`}>
                    {t.totalTanks}: {tankIdsInput.split(',').filter(id => id.trim()).length}
                  </p>
                </div>

                <div className={`flex gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <Button
                    data-testid="submit-permission-button"
                    onClick={handleCreatePermission}
                    disabled={submitting}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    {submitting ? t.creating : t.createPermission}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1"
                  >
                    {t.cancel}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative">
          <Search className={`absolute ${isRtl ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5`} />
          <Input
            data-testid="search-permissions-input"
            type="text"
            placeholder={t.searchPermissions}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${isRtl ? 'pr-10 text-right' : 'pl-10'} h-11`}
            dir={isRtl ? 'rtl' : 'ltr'}
          />
        </div>

        {loading ? (
          <div className="text-center py-12">{t.loading}</div>
        ) : filteredPermissions.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-slate-500 mb-4">{t.noPermissions}</p>
            <p className="text-sm text-slate-400">{t.createFirstPermission}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPermissions.map((permission) => (
              <Card
                key={permission.permission_number}
                data-testid={`permission-card-${permission.permission_number}`}
                className="p-6 bg-white border border-slate-200 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => navigate(`/permissions/${permission.permission_number}`)}
              >
                <div className={`flex items-start justify-between mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <div className="bg-slate-900 text-white rounded-lg px-4 py-2 font-mono font-bold text-lg">
                    #{permission.permission_number}
                  </div>
                  <div className="flex items-center gap-2">
                    {permission.document_status && (
                      <span className={`status-badge ${permission.document_status}`}>
                        {t[permission.document_status] || permission.document_status}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`space-y-2 ${isRtl ? 'text-right' : ''}`}>
                  <p className="text-slate-600">
                    <span className="font-semibold text-slate-900">{permission.tank_ids.length}</span> {t.tanksAssigned}
                  </p>
                  <p className="text-sm text-slate-500">
                    {t.created}: {new Date(permission.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className={`flex gap-2 mt-4 pt-4 border-t border-slate-100 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <Button
                    data-testid={`edit-permission-${permission.permission_number}`}
                    size="sm"
                    variant="outline"
                    onClick={(e) => handleEditPermission(permission, e)}
                    className={`flex-1 text-blue-600 hover:bg-blue-50 hover:text-blue-700 ${isRtl ? 'flex-row-reverse' : ''}`}
                  >
                    <Edit className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} />
                    {t.edit}
                  </Button>
                  {userRole === 'admin' && (
                    <Button
                      data-testid={`delete-permission-${permission.permission_number}`}
                      size="sm"
                      variant="outline"
                      onClick={(e) => handleDeletePermission(permission, e)}
                      className={`flex-1 text-red-600 hover:bg-red-50 hover:text-red-700 ${isRtl ? 'flex-row-reverse' : ''}`}
                    >
                      <Trash2 className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} />
                      {t.delete}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Permission Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader className="border-b border-blue-200 pb-4">
            <DialogTitle className={`text-2xl font-bold text-blue-900 ${isRtl ? 'text-right' : ''}`}>
              {t.editPermission} #{editingPermission?.permission_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <Label className={isRtl ? 'block text-right' : ''}>{t.tankIds}</Label>
              <p className={`text-sm text-slate-600 mb-2 ${isRtl ? 'text-right' : ''}`}>
                {t.tankIdsHelper}
              </p>
              <textarea
                data-testid="edit-tank-ids-input"
                value={editTankIdsInput}
                onChange={(e) => setEditTankIdsInput(e.target.value)}
                placeholder={t.tankIdsPlaceholder}
                className={`w-full min-h-32 p-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-900 focus:border-transparent ${isRtl ? 'text-right' : ''}`}
                dir={isRtl ? 'rtl' : 'ltr'}
              />
              <p className={`text-sm text-slate-500 mt-2 ${isRtl ? 'text-right' : ''}`}>
                {t.totalTanks}: {editTankIdsInput.split(',').filter(id => id.trim()).length}
              </p>
            </div>

            <div className={`flex gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <Button
                data-testid="update-permission-submit"
                onClick={handleUpdatePermission}
                disabled={submitting}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
              >
                {submitting ? t.updating : t.updatePermission}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingPermission(null);
                  setEditTankIdsInput('');
                }}
                className="flex-1"
              >
                {t.cancel}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Permissions;
